/**
 * Macro #30 — Social Layer V1 API client.
 *
 * Purely additive. Reads carry the SIWE session cookie (`credentials: include`)
 * and are viewer-aware (optional session). Mutations reuse the FROZEN owner-write
 * authority: the memory-only session-bound CSRF token (shared with
 * territory-identity-api) + the browser Origin, refetching the token once on a
 * stale-token 403. The frontend never fabricates social state.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class SocialApiError extends Error {
  code: string;
  status: number;
  details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "SocialApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new SocialApiError(resp.status, code, message, (env as { details?: unknown }).details);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function send<T>(method: "POST" | "PATCH", path: string, body?: Record<string, unknown>): Promise<T> {
  const payload = JSON.stringify(body ?? {});
  const doSend = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: payload,
    });
  let resp = await doSend(await getCsrfToken());
  if (resp.status === 403) {
    const peek = (await resp.clone().json().catch(() => null)) as ErrorEnvelope | null;
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doSend(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

// ─────────────────────────────── types ───────────────────────────────
export interface ProfileLink {
  label: string;
  url: string;
}

export interface SelfProfile {
  wallet: string;
  handle: string | null;
  links: ProfileLink[];
  handle_changed_at: string | null;
}

export interface PublicProfile {
  wallet: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  links: ProfileLink[];
  status: string;
  badge: unknown;
  followers: number;
  following: number;
  historical_likes_total: number;
  historical_comments_total: number;
  territories_owned: number;
  territories_published: number;
  purchased_count: number;
  recent_activity: Array<Record<string, unknown>>;
}

export interface FollowStatus {
  following: boolean;
  followers: number;
  creator_following: number;
}

export interface EdgeItem {
  wallet: string;
  handle: string | null;
  created_at: string;
}

export interface PageOf<T> {
  items: T[];
  next_cursor: string | null;
}

export interface LikeState {
  count: number;
  liked_by_me: boolean;
}

export interface Comment {
  id: string;
  territory_identity_id: string;
  author_wallet: string;
  body: string;
  parent_comment_id: string | null;
  status: string;
  like_count: number;
  reply_count: number;
  created_at: string;
  liked_by_me: boolean;
  is_mine: boolean;
}

export interface FeedItem {
  type: string;
  actor_wallet?: string;
  actor_handle?: string | null;
  owner_wallet?: string;
  owner_handle?: string | null;
  identity_id?: string;
  artwork_id?: string;
  parcel_id?: number;
  title?: string | null;
  like_count?: number;
  ts: string | null;
  deep_link: string;
}

export type ReportTargetType = "profile" | "comment" | "territory";
export type ReportReason =
  | "spam" | "abuse" | "harassment" | "hate" | "nsfw" | "scam" | "impersonation" | "other";

// ─────────────────────────────── client ──────────────────────────────
export const socialApi = {
  // profiles
  myProfile: (signal?: AbortSignal) => get<SelfProfile>("/api/v1/social/me/profile", signal),
  setHandle: (handle: string) => send<SelfProfile>("PATCH", "/api/v1/social/me/profile/handle", { handle }),
  setLinks: (links: ProfileLink[]) => send<SelfProfile>("PATCH", "/api/v1/social/me/profile/links", { links }),
  publicProfile: (handleOrWallet: string, signal?: AbortSignal) =>
    get<PublicProfile>(`/api/v1/social/profile/${encodeURIComponent(handleOrWallet)}`, signal),

  // follows
  follow: (target: { creator_wallet?: string; territory_identity_id?: string }) =>
    send<{ ok: boolean; following: boolean; created: boolean }>("POST", "/api/v1/social/follow", target),
  unfollow: (target: { creator_wallet?: string; territory_identity_id?: string }) =>
    send<{ ok: boolean; following: boolean; removed: number }>("POST", "/api/v1/social/unfollow", target),
  followStatus: (creator: string, signal?: AbortSignal) =>
    get<FollowStatus>(`/api/v1/social/me/follow-status?creator=${encodeURIComponent(creator)}`, signal),
  followers: (wallet: string, cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<EdgeItem>>(`/api/v1/social/followers/${encodeURIComponent(wallet)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),
  following: (wallet: string, cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<EdgeItem>>(`/api/v1/social/following/${encodeURIComponent(wallet)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),

  // likes
  likeTerritory: (identityId: string) =>
    send<{ ok: boolean; liked: boolean; count: number }>("POST", `/api/v1/social/territory/${encodeURIComponent(identityId)}/like`),
  unlikeTerritory: (identityId: string) =>
    send<{ ok: boolean; liked: boolean; count: number }>("POST", `/api/v1/social/territory/${encodeURIComponent(identityId)}/unlike`),
  territoryLikes: (identityId: string, signal?: AbortSignal) =>
    get<LikeState>(`/api/v1/social/territory/${encodeURIComponent(identityId)}/likes`, signal),

  // saves
  addSave: (identityId: string) =>
    send<{ ok: boolean; saved: boolean }>("POST", "/api/v1/social/saves", { territory_identity_id: identityId }),
  removeSave: (identityId: string) =>
    send<{ ok: boolean; saved: boolean; removed: number }>("POST", "/api/v1/social/saves/remove", { territory_identity_id: identityId }),
  mySaves: (cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<{ territory_identity_id: string; created_at: string; deep_link: string }>>(
      `/api/v1/social/me/saves${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),

  // comments
  listComments: (identityId: string, cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<Comment>>(`/api/v1/social/territory/${encodeURIComponent(identityId)}/comments${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),
  listReplies: (commentId: string, cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<Comment>>(`/api/v1/social/comments/${encodeURIComponent(commentId)}/replies${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),
  createComment: (identityId: string, body: string, parentId?: string | null) =>
    send<Comment>("POST", `/api/v1/social/territory/${encodeURIComponent(identityId)}/comments`,
      { body, parent_comment_id: parentId ?? null }),
  likeComment: (commentId: string) =>
    send<{ ok: boolean; liked: boolean; like_count: number }>("POST", `/api/v1/social/comments/${encodeURIComponent(commentId)}/like`),
  unlikeComment: (commentId: string) =>
    send<{ ok: boolean; liked: boolean; like_count: number }>("POST", `/api/v1/social/comments/${encodeURIComponent(commentId)}/unlike`),
  removeComment: (commentId: string) =>
    send<{ ok: boolean; status: string }>("POST", `/api/v1/social/comments/${encodeURIComponent(commentId)}/remove`),
  hideComment: (commentId: string) =>
    send<{ ok: boolean; status: string }>("POST", `/api/v1/social/comments/${encodeURIComponent(commentId)}/hide`),

  // blocks / reports
  block: (wallet: string) => send<{ ok: boolean; blocked: boolean }>("POST", "/api/v1/social/block", { wallet }),
  unblock: (wallet: string) => send<{ ok: boolean; blocked: boolean; removed: number }>("POST", "/api/v1/social/unblock", { wallet }),
  myBlocks: (signal?: AbortSignal) => get<{ items: { wallet: string; created_at: string }[] }>("/api/v1/social/me/blocks", signal),
  report: (payload: { target_type: ReportTargetType; target_id: string; reason: ReportReason; note?: string }) =>
    send<{ ok: boolean; reported: boolean; created: boolean }>("POST", "/api/v1/social/reports", payload),

  // feeds
  feedFollowing: (cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<FeedItem>>(`/api/v1/social/feed/following${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),
  feedDiscover: (cursor?: string | null, signal?: AbortSignal) =>
    get<PageOf<FeedItem>>(`/api/v1/social/feed/discover${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, signal),
};

/** Short 0x…abcd wallet label. */
export function shortWallet(w: string | null | undefined): string {
  if (!w) return "";
  return w.length > 10 ? `${w.slice(0, 6)}…${w.slice(-4)}` : w;
}

/** Preferred display label: handle if present, else short wallet. */
export function creatorLabel(handle: string | null | undefined, wallet: string | null | undefined): string {
  return handle ? `@${handle}` : shortWallet(wallet);
}
